import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  type CreateLeadSourceConnectionPayload,
  leadSourcesAPI,
  type UpdateLeadSourceConnectionPayload,
} from '../lib/leadSourcesApi';
import { Sentry } from '../lib/sentry';

const CONNECTIONS_KEY = 'leadSourceConnections';
const CATALOG_KEY = 'leadSourceCatalog';

export function useLeadSourceConnections(organizationId: string | undefined) {
  return useQuery({
    queryKey: [CONNECTIONS_KEY, organizationId],
    queryFn: async () => {
      const { data } = await leadSourcesAPI.list(organizationId as string);
      return data;
    },
    enabled: !!organizationId,
  });
}

export function useLeadSourceConnection(
  organizationId: string | undefined,
  id: string | undefined,
) {
  return useQuery({
    queryKey: [CONNECTIONS_KEY, organizationId, id],
    queryFn: async () => {
      const { data } = await leadSourcesAPI.getOne(organizationId as string, id as string);
      return data;
    },
    enabled: !!organizationId && !!id,
  });
}

// A 401/403 here means the credential-authenticated intake path is not what's
// failing (this is a plain authenticated GET) — retrying can't fix a rejected
// session, so we stop immediately instead of masking it behind a transient
// "loading" state.
function isAuthError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403;
}

export function useLeadSourceCatalog() {
  const query = useQuery({
    queryKey: [CATALOG_KEY],
    queryFn: async () => {
      const { data } = await leadSourcesAPI.catalog();
      return data;
    },
    retry: (failureCount, error) => (isAuthError(error) ? false : failureCount < 2),
  });

  // Report only once the bounded retries above are exhausted — `isError`
  // reflects that final state, not each individual attempt — so one outage
  // produces one Sentry event instead of one per retry.
  useEffect(() => {
    if (query.isError) {
      Sentry.captureException(query.error);
    }
  }, [query.isError, query.error]);

  return query;
}

export function useCreateLeadSourceConnection() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (payload: CreateLeadSourceConnectionPayload) => {
      const organizationId = currentOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      const { data } = await leadSourcesAPI.create(organizationId, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY, currentOrganization?.id] });
    },
  });
}

export function useUpdateLeadSourceConnection() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateLeadSourceConnectionPayload;
    }) => {
      const organizationId = currentOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      const { data } = await leadSourcesAPI.update(organizationId, id, payload);
      return data;
    },
    onSuccess: (_data, variables) => {
      const organizationId = currentOrganization?.id;
      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY, organizationId] });
      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY, organizationId, variables.id] });
    },
  });
}

export function useRotateLeadSourceCredential() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const organizationId = currentOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      const { data } = await leadSourcesAPI.rotateCredential(organizationId, id);
      return data;
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: [CONNECTIONS_KEY, currentOrganization?.id, id],
      });
    },
  });
}

export function useRevokeLeadSourceConnection() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const organizationId = currentOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      await leadSourcesAPI.revoke(organizationId, id);
    },
    onSuccess: (_data, id) => {
      const organizationId = currentOrganization?.id;
      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY, organizationId] });
      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY, organizationId, id] });
    },
  });
}

export function useDeleteLeadSourceConnection() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const organizationId = currentOrganization?.id;
      if (!organizationId) throw new Error('No organization selected');
      await leadSourcesAPI.remove(organizationId, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONNECTIONS_KEY, currentOrganization?.id] });
    },
  });
}
